"use client";

import type { Dispatch, SetStateAction } from "react";
import { useCallback, useMemo, useState } from "react";
import type { SelectedThought, Target } from "@/generated/fastapi";
import { useThoughtArrangementPreferences } from "@/state/globalStore";
import { applyThoughtOrdering } from "../../utils";
import useSelectedThoughtDocumentsQuery from "../../queries/useSelectedThoughtDocumentsQuery";
import useSelectedThoughtRerankQuery from "../../queries/useSelectedThoughtRerankQuery";
import type { SelectedThoughtDocument } from "../../types";

export default function useStudioArrangementState({
  selectedThoughts,
  rerankTarget,
  scopeKey,
  sourceKeyPayload,
}: {
  selectedThoughts: SelectedThought[];
  rerankTarget?: Target | null | undefined;
  scopeKey: string;
  sourceKeyPayload: Record<string, unknown>;
}) {
  const [localArrangementState, setLocalArrangementState] = useState<{
    sourceKey: string;
    thoughts: SelectedThoughtDocument[] | null;
  }>({
    sourceKey: "",
    thoughts: null,
  });
  const {
    thoughtArrangementManualOverrides,
    setThoughtArrangementManualOverride,
  } = useThoughtArrangementPreferences();

  const hasManuallyReordered = Boolean(
    thoughtArrangementManualOverrides[scopeKey],
  );

  const rerankQuery = useMemo(() => {
    if (!rerankTarget || hasManuallyReordered) {
      return null;
    }

    return rerankTarget.content;
  }, [hasManuallyReordered, rerankTarget]);

  const thoughtDocumentsQuery = useSelectedThoughtDocumentsQuery(
    {
      selectedThoughts,
    },
    {
      enabled: selectedThoughts.length > 0,
    },
  );
  const rerankResultsQuery = useSelectedThoughtRerankQuery(
    {
      selectedThoughts,
    },
    rerankQuery,
    {
      enabled: selectedThoughts.length > 0,
    },
  );

  const orderedThoughts = useMemo(
    () =>
      applyThoughtOrdering(
        thoughtDocumentsQuery.data ?? [],
        rerankResultsQuery.data ?? [],
        selectedThoughts,
        Boolean(rerankQuery),
      ),
    [
      thoughtDocumentsQuery.data,
      rerankResultsQuery.data,
      rerankQuery,
      selectedThoughts,
    ],
  );

  const sourceKey = useMemo(
    () =>
      JSON.stringify({
        ...sourceKeyPayload,
        rerankQuery,
        fetchedThoughtIds: (thoughtDocumentsQuery.data ?? []).map(
          (thought) => thought.objectID,
        ),
      }),
    [sourceKeyPayload, thoughtDocumentsQuery.data, rerankQuery],
  );

  const sortedThoughts =
    localArrangementState.sourceKey === sourceKey &&
    localArrangementState.thoughts !== null
      ? localArrangementState.thoughts
      : orderedThoughts;

  const setSortedThoughts = useCallback<
    Dispatch<SetStateAction<SelectedThoughtDocument[]>>
  >(
    (nextThoughts) => {
      const resolvedThoughts =
        typeof nextThoughts === "function"
          ? nextThoughts(sortedThoughts)
          : nextThoughts;

      setLocalArrangementState({
        sourceKey,
        thoughts: resolvedThoughts,
      });
    },
    [sortedThoughts, sourceKey],
  );

  const setHasManuallyReordered = useCallback(
    (nextValue: SetStateAction<boolean>) => {
      const resolvedValue =
        typeof nextValue === "function"
          ? nextValue(hasManuallyReordered)
          : nextValue;

      setThoughtArrangementManualOverride(scopeKey, resolvedValue);
    },
    [hasManuallyReordered, scopeKey, setThoughtArrangementManualOverride],
  );

  return {
    isLoading: thoughtDocumentsQuery.isLoading || rerankResultsQuery.isLoading,
    sortedThoughts,
    setSortedThoughts,
    setHasManuallyReordered,
  };
}
